import { type Framework } from '@toeverything/infra';

import { WorkspaceServerService } from '../cloud';
import { DocScope, DocService } from '../doc';
import { WorkspaceShareSettingService } from '../share-setting';
import { DocViewStateService } from '../streamlain/services/doc-view-state';
import { FullscreenRegistry } from '../streamlain/services/fullscreen-registry';
import { WorkspaceScope, WorkspaceService } from '../workspace';
import { Editor } from './entities/editor';
import { EditorScope } from './scopes/editor';
import { EditorService } from './services/editor';
import { EditorsService } from './services/editors';
import { FollowerInputService } from './services/follower-input';
import { FollowerSettingsSyncService } from './services/follower-settings-sync';
import { FollowerViewportSyncService } from './services/follower-viewport-sync';
import { FullscreenService } from './services/fullscreen.service';
import { ModerationViewStateService } from './services/moderation-view-state.service';
import { PresentationStateMachine } from './services/presentation-state-machine';
import { WorkspaceRealtimeService } from './services/workspace-realtime.service';

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export { Editor } from './entities/editor';
export { EditorScope } from './scopes/editor';
export { EditorService } from './services/editor';
export { EditorsService } from './services/editors';
export { FollowerInputService } from './services/follower-input';
export { FollowerSettingsSyncService } from './services/follower-settings-sync';
export { FollowerViewportSyncService } from './services/follower-viewport-sync';
export { FullscreenService } from './services/fullscreen.service';
export { ModerationViewStateService } from './services/moderation-view-state.service';
export { PresentationStateMachine } from './services/presentation-state-machine';
export {
  type DocParticipantRole,
  WorkspaceRealtimeService,
} from './services/workspace-realtime.service';

// Type exports
export type {
  EditorSelector,
  IPresentationBroadcaster,
  IViewportController,
  PresentationMachineState,
  PresentationMode,
  PresentationSettings,
  PresentationState,
  PresenterInfo,
  TransitionResult,
  ViewportState,
} from './types';
export { StateMachineHelpers } from './types';

// ═══════════════════════════════════════════════════════════════════════════
// MODULE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

export function configureEditorModule(framework: Framework) {
  framework
    .scope(WorkspaceScope)

    // ═══════════════════════════════════════════════════════════════════════
    // REALTIME SERVICE (sl-jmyl.6: WorkspaceScope — one socket per workspace)
    // Handles: Presentation events, Workspace Presence, Moderation state
    // ═══════════════════════════════════════════════════════════════════════
    .service(WorkspaceRealtimeService, [
      WorkspaceService,
      WorkspaceServerService,
    ])

    .scope(DocScope)
    .service(EditorsService)

    // ═══════════════════════════════════════════════════════════════════════
    // STATE MACHINE (SINGLE SOURCE OF TRUTH for UI state)
    // Now uses WorkspaceRealtimeService from outer WorkspaceScope
    // ═══════════════════════════════════════════════════════════════════════
    .service(PresentationStateMachine, [DocService, WorkspaceRealtimeService])

    // ═══════════════════════════════════════════════════════════════════════
    // AUXILIARY SERVICES (depend on State Machine)
    // ═══════════════════════════════════════════════════════════════════════

    // Settings sync for followers (reads presenter settings)
    .service(FollowerSettingsSyncService, [PresentationStateMachine])

    // Fullscreen management
    .service(FullscreenService)

    // Derived UI contract for all moderation floating surfaces.
    .service(ModerationViewStateService, [
      DocService,
      WorkspaceRealtimeService,
      PresentationStateMachine,
      FullscreenService,
    ])

    // Viewport sync for followers
    .service(FollowerViewportSyncService, [
      PresentationStateMachine,
      FullscreenService,
      WorkspaceRealtimeService,
    ])

    // Input locking for followers
    .service(FollowerInputService, [
      PresentationStateMachine,
      FullscreenService,
      WorkspaceRealtimeService,
    ])

    // ═══════════════════════════════════════════════════════════════════════
    // EDITOR ENTITY
    // ═══════════════════════════════════════════════════════════════════════
    .entity(Editor, [
      DocService,
      WorkspaceService,
      PresentationStateMachine,
      FollowerViewportSyncService,
      FollowerInputService,
      FullscreenService,
      WorkspaceRealtimeService,
      DocViewStateService,
      FullscreenRegistry,
      WorkspaceShareSettingService,
    ])
    .scope(EditorScope)
    .service(EditorService, [EditorScope]);
}
